import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import type { EnrichmentAttribute } from "./types";

interface ProductEnrichmentTableProps {
  productKey: string;
  attributes: EnrichmentAttribute[];
}

export function ProductEnrichmentTable({ productKey, attributes }: ProductEnrichmentTableProps) {
  const { t } = useTranslation();
  const p = `productPages.${productKey}`;

  return (
    <section className="py-20 lg:py-28 bg-secondary/30">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
            {t(`${p}.enrichmentData.title`)}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground font-body">
            {t(`${p}.enrichmentData.subtitle`)}
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-px bg-border">
              {/* Header - hidden on mobile where grid stacks */}
              <div className="hidden sm:block bg-primary px-6 py-3">
                <span className="text-sm font-bold font-subheading text-primary-foreground uppercase tracking-wide">
                  Attribut
                </span>
              </div>
              <div className="hidden sm:block bg-primary px-6 py-3">
                <span className="text-sm font-bold font-subheading text-primary-foreground uppercase tracking-wide">
                  Beskrivning
                </span>
              </div>

              {/* Rows */}
              {attributes.map((attr, index) => (
                <motion.div
                  key={attr.nameKey}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.03 }}
                  className="contents"
                >
                  <div className={`px-6 py-3.5 ${index % 2 === 0 ? "bg-card" : "bg-secondary/40"}`}>
                    <span className="text-sm font-bold font-subheading text-foreground">
                      {t(attr.nameKey)}
                    </span>
                  </div>
                  <div className={`px-6 py-3.5 sm:pt-3.5 pt-0 pb-4 ${index % 2 === 0 ? "bg-card" : "bg-secondary/40"}`}>
                    <span className="text-sm text-muted-foreground font-body">
                      {t(attr.descriptionKey)}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
